import type { SVGProps } from 'react';
import {
  getRelationAppearance,
  RELATION_ENDPOINT_BASE_RATIO,
  RELATION_ENDPOINT_GEOMETRY,
  RELATION_LINE_WIDTH,
  type RelationEndpoint,
} from '../lib/relation-appearance';

const Endpoint = ({
  kind,
  testId,
}: {
  kind: RelationEndpoint;
  testId: string;
}) => {
  if (kind === 'none') return null;
  const geometry = RELATION_ENDPOINT_GEOMETRY[kind];
  const x = geometry.length / 2;
  const y = geometry.width / 2;
  switch (kind) {
    case 'vee':
      return (
        <path
          d={`M${-x} ${-y}L${x} 0L${-x} ${y}L${geometry.length * RELATION_ENDPOINT_GEOMETRY.vee.notch - x} 0Z`}
          fill='currentColor'
          stroke='none'
          data-testid={testId}
        />
      );
    case 'circle':
      return (
        <circle r={x} fill='currentColor' stroke='none' data-testid={testId} />
      );
    case 'diamond':
    case 'hollowDiamond':
      return (
        <path
          d={`M${-x} 0L0 ${-y}L${x} 0L0 ${y}Z`}
          fill={kind === 'diamond' ? 'currentColor' : 'none'}
          stroke={kind === 'diamond' ? 'none' : 'currentColor'}
          strokeWidth={RELATION_ENDPOINT_GEOMETRY.hollowDiamond.outline}
          data-testid={testId}
        />
      );
    case 'hollowTriangle':
      return (
        <path
          d={`M${-x} ${-y}L${x} 0L${-x} ${y}Z`}
          strokeWidth={RELATION_ENDPOINT_GEOMETRY.hollowTriangle.outline}
          data-testid={testId}
        />
      );
  }
};

const endpointLength = (kind: RelationEndpoint) =>
  kind === 'none'
    ? 0
    : RELATION_ENDPOINT_GEOMETRY[kind].length * RELATION_LINE_WIDTH;

export function RelationGlyph({
  relation,
  inherited = false,
  markerOnly = false,
  ...props
}: SVGProps<SVGSVGElement> & {
  relation?: string;
  inherited?: boolean;
  markerOnly?: boolean;
}) {
  const { head, tail, dashSize, gapSize } = getRelationAppearance(
    relation,
    inherited,
  );
  const start = 2 + endpointLength(tail) * (1 - RELATION_ENDPOINT_BASE_RATIO);
  const end = 32 - endpointLength(head) * (1 - RELATION_ENDPOINT_BASE_RATIO);
  return (
    <svg
      width={markerOnly ? 16 : 34}
      height={markerOnly ? 16 : 12}
      viewBox={markerOnly ? '0 0 16 16' : '0 0 34 12'}
      fill='none'
      stroke='currentColor'
      strokeWidth={RELATION_LINE_WIDTH}
      strokeLinecap='butt'
      strokeLinejoin='miter'
      aria-hidden
      data-relation-head={head}
      data-relation-tail={tail}
      {...props}
    >
      {markerOnly ? (
        <g
          transform={`translate(8 8) rotate(-90) scale(${RELATION_LINE_WIDTH})`}
        >
          <Endpoint
            kind={tail !== 'none' ? tail : head}
            testId='relation-glyph-marker'
          />
        </g>
      ) : (
        <>
          <path
            d={`M${start} 6H${end}`}
            strokeLinecap='round'
            strokeDasharray={gapSize ? `${dashSize} ${gapSize}` : undefined}
            data-testid='relation-glyph-line'
          />
          <g
            transform={`translate(${2 + endpointLength(tail) / 2} 6) scale(${RELATION_LINE_WIDTH})`}
          >
            <Endpoint kind={tail} testId='relation-glyph-tail' />
          </g>
          <g
            transform={`translate(${32 - endpointLength(head) / 2} 6) scale(${RELATION_LINE_WIDTH})`}
          >
            <Endpoint kind={head} testId='relation-glyph-head' />
          </g>
        </>
      )}
    </svg>
  );
}